import { addAction } from '@shared/utils/logger';
import { trackFailedBundleWrites } from './failedBundleWrites';
import i18n from './i18n';

// Private i18next internals, pinned against i18next 20.6.1 (see `Connector` in dist/cjs/i18next.js).
type ReadCallback = (err: unknown, data?: unknown) => void;
type BackendConnector = Readonly<{
  load: (languages: ReadonlyArray<string>, namespaces: ReadonlyArray<string>, callback: (err?: unknown) => void) => void;
  read: (lng: string, ns: string, fcName: 'read', tried: undefined, wait: undefined, callback: ReadCallback) => void;
  loaded: (name: string, err: unknown, data?: unknown) => void;
  state: Readonly<Record<string, number>>;
}>;
type LanguageUtils = Readonly<{ toResolveHierarchy: (code: string) => string[] }>;
/** react-i18next's record of every namespace a `useTranslation` call has asked for. */
type ReportNamespaces = Readonly<{ getUsedNamespaces: () => string[] }>;
const LOAD_PENDING = 1;
const LOAD_FAILED = -1;

/**
 * Deadline for one preload. i18next-http-backend sets no request timeout, so a stalled bundle
 * request would otherwise block the switch and any chunk waiting on it forever. Same budget as
 * one chunk attempt (`RETRY_ATTEMPT_TIMEOUT_MS` in loadableFromNamedExport.ts): a bundle is
 * fetched alongside its chunk and deserves no more patience than the chunk itself.
 */
export const LANGUAGE_PRELOAD_TIMEOUT_MS = 15000;

type PreloadFailureReason = 'error' | 'timeout' | 'throw' | 'missing';
export type PreloadFailure = Readonly<{ reason: PreloadFailureReason; message: string }>;

const errorMessage = (err: unknown): string => {
  if (Array.isArray(err)) return err.map(errorMessage).join('; ');
  return String((err as { message?: string } | null | undefined)?.message ?? err);
};

/** Telemetry attributes are capped in size; the count keeps a long list comparable. */
const MAX_LISTED_NAMESPACES = 20;
export const listNamespaces = (namespaces: ReadonlyArray<string>): string =>
  namespaces.length > MAX_LISTED_NAMESPACES ? `${namespaces.slice(0, MAX_LISTED_NAMESPACES).join(',')},+${namespaces.length - MAX_LISTED_NAMESPACES} more` : namespaces.join(',');

const reportPreloadFailure = (lng: string, namespaces: ReadonlyArray<string>, failure: PreloadFailure, extra: Readonly<Record<string, unknown>> = {}): void => {
  addAction('I18nLanguagePreloadFailed', {
    lng,
    namespaces: listNamespaces(namespaces),
    reason: failure.reason,
    message: failure.message,
    ...extra,
  });
};

/** i18next 20.6.1 sets this while `changeLanguage` is loading, but doesn't type it. */
const getLanguageChangingTo = (): string | undefined => (i18n as { isLanguageChangingTo?: string }).isLanguageChangingTo;

/** The target of a `switchLanguage` still preloading, before i18next knows about the switch. */
let pendingTarget: string | undefined;

/** The language the page is about to be in: a pending switch, an in-flight change, or the current one. */
export const getEffectiveLanguage = (): string | undefined => pendingTarget ?? getLanguageChangingTo() ?? i18n.language;

const getBackendServices = () => {
  const services = i18n.services as { backendConnector?: BackendConnector; languageUtils?: LanguageUtils } | undefined;
  const connector = services?.backendConnector;
  const languageUtils = services?.languageUtils;
  return connector && languageUtils ? { connector, languageUtils } : undefined;
};

/** Whether the last load of `lng|ns` failed, even if it left an empty bundle in the store. */
const hasBundleFailed = (name: string): boolean => getBackendServices()?.connector.state[name] === LOAD_FAILED || trackFailedBundleWrites(i18n.store).has(name);

/**
 * `t()` has real strings for `ns` in `lng`: the bundle is in the store and its last load didn't fail.
 * A bundle put in the store directly (the SSR store) was never loaded, so it counts.
 */
export const isBundleUsable = (lng: string, ns: string): boolean => i18n.hasResourceBundle(lng, ns) && !hasBundleFailed(`${lng}|${ns}`);

/**
 * The namespaces `t()` can't resolve in `lng`: no usable bundle in any language of its resolve
 * hierarchy (just `lng` in prod, where `fallbackLng` is false; `lng` then en-US in dev).
 */
export const findMissingBundles = (lng: string, namespaces: ReadonlyArray<string>): string[] => {
  const languageUtils = getBackendServices()?.languageUtils;
  if (lng.toLowerCase() === 'cimode') return [];
  const languages = languageUtils ? languageUtils.toResolveHierarchy(lng) : [lng];
  return namespaces.filter(ns => !languages.some(l => isBundleUsable(l, ns)));
};

/**
 * Loads `namespaces` for `lng` and resolves with what went wrong, or `undefined`. No telemetry.
 * Stops waiting after `timeoutMs` and marks what is still pending as failed, so `changeLanguage`
 * and `useTranslation` stop waiting too; a response that arrives later still lands in the store.
 *
 * Bundles that failed before are requested again, including one a 5xx left as an empty bundle.
 * i18next never retries one on its own (a failed bundle stays `-1`, even through `reloadResources`),
 * so without this one bad response would pin it as missing for the rest of the page's life. They
 * are re-read straight from the backend rather than reset to pending: a pending bundle makes
 * react-i18next suspend every mounted component that uses it, and guest pages have no `<Suspense>`
 * to catch that.
 */
export const loadBundles = (lng: string, namespaces: ReadonlyArray<string>, timeoutMs: number = LANGUAGE_PRELOAD_TIMEOUT_MS): Promise<PreloadFailure | undefined> => {
  const backend = getBackendServices();
  if (!backend || namespaces.length === 0 || lng.toLowerCase() === 'cimode') {
    return Promise.resolve(undefined);
  }
  const { connector, languageUtils } = backend;
  const uniqueNamespaces = [...new Set(namespaces)];
  const languages = languageUtils.toResolveHierarchy(lng);
  const names = languages.flatMap(l => uniqueNamespaces.map(ns => `${l}|${ns}`));
  const failedBefore = names.filter(hasBundleFailed);
  return new Promise(resolve => {
    let settled = false;
    const settle = (failure?: PreloadFailure) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(failure);
    };
    const rereading = new Set(failedBefore);
    const errors: unknown[] = [];
    let outstanding = 1 + failedBefore.length;
    const done = (err?: unknown) => {
      if (err) errors.push(err);
      outstanding -= 1;
      if (outstanding === 0) settle(errors.length > 0 ? { reason: 'error', message: errorMessage(errors) } : undefined);
    };
    const timer = setTimeout(() => {
      const stalled = names.filter(name => connector.state[name] === LOAD_PENDING);
      // Settle first: marking a bundle failed runs the load callback below synchronously.
      settle({ reason: 'timeout', message: `stalled after ${timeoutMs}ms: ${[...stalled, ...rereading].join(',')}` });
      stalled.forEach(name => connector.loaded(name, new Error('i18n preload timed out')));
    }, timeoutMs);
    try {
      failedBefore.forEach(name => {
        const [l, ns] = name.split('|');
        connector.read(l, ns, 'read', undefined, undefined, (err, data) => {
          rereading.delete(name);
          connector.loaded(name, err, data);
          done(err);
        });
      });
      connector.load(languages, uniqueNamespaces, err => done(err));
    } catch (err) {
      settle({ reason: 'throw', message: errorMessage(err) });
    }
  });
};

/**
 * Loads `namespaces` for `lng` without touching the current language. Always resolves: failures
 * are reported as `I18nLanguagePreloadFailed` and left as i18next's "loaded with error" state, so
 * `useTranslation` renders instead of waiting on them.
 *
 * i18next 20 has no public API for this: `loadLanguages` permanently adds `lng` to
 * `options.preload`, `reloadResources` refetches bundles already in the store. The connector's
 * `load` is what `changeLanguage` itself uses, and it dedupes against loaded/pending bundles.
 */
export const loadForLanguage = async (lng: string, namespaces: ReadonlyArray<string>): Promise<void> => {
  const failure = await loadBundles(lng, namespaces);
  if (failure) reportPreloadFailure(lng, [...new Set(namespaces)], failure);
};

/** Resolves once an in-flight `changeLanguage` has landed, so ours is applied after it, not before. */
const waitForInFlightChange = (): Promise<void> => {
  if (getLanguageChangingTo() === undefined) return Promise.resolve();
  return new Promise(resolve => {
    const done = () => {
      i18n.off('languageChanged', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, LANGUAGE_PRELOAD_TIMEOUT_MS);
    i18n.on('languageChanged', done);
  });
};

let latestSwitch = 0;

/**
 * The namespaces the page has rendered with: react-i18next records every `useTranslation`
 * namespace, including ours (it wraps react-i18next's). Not the store and not `options.ns`: on SSR
 * pages `useSSR` fills both with the whole server store (~200 namespaces), almost all unused.
 * Empty before the first render, and then nothing on screen can break.
 */
const getUsedNamespaces = (): string[] => [...new Set((i18n as { reportNamespaces?: ReportNamespaces }).reportNamespaces?.getUsedNamespaces() ?? [])];

/** Everything `changeLanguage` would load (`options.ns`), plus namespaces added to the store directly. */
const getKnownNamespaces = (current: string | undefined): string[] => {
  const configured = i18n.options?.ns ?? [];
  const namespacesInStore = current ? Object.keys(i18n.store?.data?.[current] ?? {}) : [];
  return [...new Set([...(typeof configured === 'string' ? [configured] : configured), ...namespacesInStore])];
};

/**
 * `i18n.changeLanguage` that switches only once `t()` can resolve every namespace the page uses in
 * the target language. A component whose bundle is missing after the flip suspends — and guest
 * pages hydrate with no root `<Suspense>`, so that suspension crashes into the error boundary. When
 * a used bundle stalls past the deadline, fails, or loads no data, the page stays in its current
 * language and the call resolves `false`. There is no automatic retry: the next call (a later
 * syncer run, the guest picking again) requests the failed bundles again.
 *
 * Steps (the two loads share one `LANGUAGE_PRELOAD_TIMEOUT_MS` budget):
 * 1. Load the used namespaces. Missing any → stop. Nothing else is requested.
 * 2. Load the rest of the store (`getKnownNamespaces`). `changeLanguage` loads these anyway, but
 *    with no deadline; a failure here is reported and doesn't stop the switch.
 * 3. Check the used namespaces again — components that mounted meanwhile count too — then flip.
 *
 * Resolves `true` when the target is applied, or already current or pending (a pending switch's
 * own caller learns whether it lands). A call superseded by a newer one resolves `false` without
 * changing the language. A change i18next has already started can't be cancelled, so it lands
 * first and the newer target is applied after it. Never rejects.
 */
export const switchLanguage = async (target: string): Promise<boolean> => {
  if (target === getEffectiveLanguage()) {
    return true;
  }
  const token = ++latestSwitch;
  const isSuperseded = () => token !== latestSwitch;
  const current = i18n.language;
  const deadline = Date.now() + LANGUAGE_PRELOAD_TIMEOUT_MS;
  pendingTarget = target;
  /** Reports a used namespace that `t()` can't resolve in `target`; `true` means don't switch. */
  const blockedByMissing = (phase: 'used' | 'final', used: ReadonlyArray<string>, failure: PreloadFailure | undefined): boolean => {
    const missing = findMissingBundles(target, used);
    if (failure || missing.length > 0) {
      reportPreloadFailure(target, used, failure ?? { reason: 'missing', message: '' }, {
        phase,
        missing: listNamespaces(missing),
        missingCount: missing.length,
        switched: missing.length === 0 && !isSuperseded(),
      });
    }
    return missing.length > 0;
  };
  try {
    const used = getUsedNamespaces();
    if (blockedByMissing('used', used, await loadBundles(target, used)) || isSuperseded()) return false;

    const usedSet = new Set(used);
    const rest = getKnownNamespaces(current).filter(ns => !usedSet.has(ns));
    const restFailure = await loadBundles(target, rest, Math.max(0, deadline - Date.now()));
    if (restFailure) reportPreloadFailure(target, rest, restFailure, { phase: 'rest', switched: !isSuperseded() });
    if (isSuperseded()) return false;
    await waitForInFlightChange();
    if (isSuperseded()) return false;
    if (blockedByMissing('final', getUsedNamespaces(), undefined)) return false;
    if (i18n.language !== target) await i18n.changeLanguage(target);
    return !isSuperseded();
  } catch (err) {
    reportPreloadFailure(target, [], { reason: 'throw', message: errorMessage(err) }, { switched: false });
    return false;
  } finally {
    if (!isSuperseded()) pendingTarget = undefined;
  }
};

/** Test hook: forget pending and superseded switches left by a previous test. */
const resetSwitchState = (): void => {
  pendingTarget = undefined;
  latestSwitch = 0;
};

export const __testing__ = { resetSwitchState };
